import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertIcon,
  Box, Button, Flex, FormControl, FormLabel, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Select, SimpleGrid, Spinner, Tbody, Td, Text, Textarea, Th, Thead, Tr, useToast,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import {
  createSale, FiscalIssuer, FiscalParty, getFiscalIssuers, getSaleProducts, getSaleRequest, getSaleSource, previewSale,
  SaleProduct, SalePreview, SaleRequest, SaleSource, updateSale,
} from "../../../services/fiscalService";
import { getBillingSetting } from "../../../services/billingSettingService";
import { getClientBranches } from "../../../services/commercialClientService";
import { crm } from "../../../theme";
import { brl } from "../../process/components/duimp";
import { formatNcm, TableCard, tdProps, thProps } from "../../process/components/tableStyles";
import { decimalInput, parseDecimal, parseWeight, statusInfo } from "../../process/components/fiscalLabels";
import FiscalPartyFields, { emptyParty, lookupParty } from "../../process/components/FiscalPartyFields";
import ClientBranchPicker, { ClientSelection, emptySelection } from "../../simulator/ClientBranchPicker";

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="13px" fontWeight="700" mt="18px" mb="8px">{children}</Text>
);

type Row = {
  key: string;
  sourceItemNumber?: number | null;
  productCode: string;
  description: string;
  ncm: string;
  cest?: string | null;
  unit: string;
  origin?: number | null;
  additionalInfo?: string | null;
  unitCost?: number;
  remaining?: number;
  quantity: string;
  unitPrice: string;
};

type Installment = { key: string; dueDate: string; value: string };

const PAYMENT_METHODS: [string, string][] = [
  ["15", "15 · boleto"], ["17", "17 · PIX"], ["01", "01 · dinheiro"], ["03", "03 · cartão de crédito"], ["04", "04 · cartão de débito"],
  ["05", "05 · crédito loja"], ["99", "99 · outros"], ["90", "90 · sem pagamento"],
];

let seq = 0;
const newKey = () => `k${++seq}`;
const fmtQty = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 4 });
/** Data do input (yyyy-mm-dd) → ISO "wall-clock" (o backend não aceita só data). */
const isoDate = (d: string) => `${d}T00:00:00`;
const addDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const bad = (n: number | null) => n !== null && (Number.isNaN(n) || n < 0);
/** Valor em R$: "1.250" (ponto + 3 casas, sem vírgula) é ambíguo (1,25 ou 1250?) e vira inválido, igual peso. */
const parseMoney = (v: string) => (/^\d{1,3}\.\d{3}$/.test(v.trim()) ? NaN : parseDecimal(v));

/**
 * Venda da Distribuidora: própria (produtos já importados) ou encomenda (sai da NF de entrada autorizada, com saldo).
 * Cliente + filial + parametrização fiscal escolhem as alíquotas; o servidor calcula os tributos (prévia ao vivo).
 * Gera/atualiza o rascunho; a emissão é na tela da nota.
 */
const SaleModal = ({ entryId, invoiceId, onClose, onSaved }: {
  /** Venda encomenda a partir desta NF de entrada. */
  entryId?: string | null;
  /** Editar a venda (rascunho/recusada) gerada por esta tela. */
  invoiceId?: string | null;
  onClose: () => void;
  onSaved: (invoiceId: string) => void;
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [issuers, setIssuers] = useState<FiscalIssuer[]>([]);
  const [issuerId, setIssuerId] = useState("");
  const [sourceId, setSourceId] = useState<string | null>(entryId ?? null);
  const [source, setSource] = useState<SaleSource | null>(null);
  const [selection, setSelection] = useState<ClientSelection>(emptySelection);
  const [recipient, setRecipient] = useState<FiscalParty>(emptyParty());
  const [withCarrier, setWithCarrier] = useState(false);
  const [carrier, setCarrier] = useState<FiscalParty>(emptyParty());
  const [rows, setRows] = useState<Row[]>([]);
  const [form, setForm] = useState({
    freightMode: 1, freight: "", insurance: "", discount: "", otherExpenses: "", volumeQuantity: "", volumeKind: "",
    netWeight: "", grossWeight: "", paymentMethod: "15", customerOrder: "", additionalInfo: "",
  });
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [fiscoText, setFiscoText] = useState("");
  const [fiscoEdited, setFiscoEdited] = useState(false);
  const [preview, setPreview] = useState<SalePreview | null>(null);
  /** Pedido (JSON) a que a prévia corresponde: com a tela mudando, a prévia antiga não vale. */
  const [previewKey, setPreviewKey] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<SaleProduct[]>([]);
  const [split, setSplit] = useState({ count: "3", every: "30" });
  const cancelRef = useRef<HTMLButtonElement>(null);
  const lastBranch = useRef<string | null>(null);
  /** A pessoa trocou cliente/filial: na edição, só aí a filial busca o destinatário (auto-seleção não sobrescreve o salvo). */
  const userPickedBranch = useRef(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const previewSeq = useRef(0);

  const onOrder = !!sourceId;
  const kind = onOrder ? "encomenda" : "venda";
  const setting = selection.settings[kind] ?? null;
  const touch = () => setDirty(true);
  const set = (field: keyof typeof form, value: any) => { touch(); setForm((f) => ({ ...f, [field]: value })); };

  const rowsFromSource = (s: SaleSource, quantities?: Record<number, { q: number; p: number }>): Row[] =>
    s.items.map((it) => ({
      key: newKey(), sourceItemNumber: it.itemNumber, productCode: it.productCode, description: it.description, ncm: it.ncm,
      cest: it.cest, unit: it.unit, origin: it.origin, additionalInfo: it.additionalInfo, unitCost: it.unitCost, remaining: it.remaining,
      quantity: quantities?.[it.itemNumber] ? decimalInput(quantities[it.itemNumber].q) : "",
      unitPrice: quantities?.[it.itemNumber] ? decimalInput(quantities[it.itemNumber].p) : "",
    }));

  // Carga: emitentes, (edição) o que foi mandado, (encomenda) a NF de entrada com saldo
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = (await getFiscalIssuers()).filter((i) => i.enabled);
        if (!active) return;
        setIssuers(list);
        const request = invoiceId ? await getSaleRequest(invoiceId) : null;
        const entry = request?.sourceInvoiceId ?? entryId ?? null;
        const src = entry ? await getSaleSource(entry) : null;
        if (!active) return;
        setSourceId(entry);
        setSource(src);
        if (request) {
          // Edição: o saldo da própria venda volta para a tela (o servidor desconta só as outras)
          const own: Record<number, { q: number; p: number }> = {};
          request.items.forEach((x) => { if (x.sourceItemNumber) own[x.sourceItemNumber] = { q: x.quantity, p: x.unitPrice }; });
          setIssuerId(request.issuerId);
          setRecipient(request.recipient);
          if (request.carrier) { setWithCarrier(true); setCarrier(request.carrier); }
          setForm({
            freightMode: request.freightMode, freight: decimalInput(request.freight || null), insurance: decimalInput(request.insurance || null),
            discount: decimalInput(request.discount || null), otherExpenses: decimalInput(request.otherExpenses || null),
            volumeQuantity: request.volumeQuantity?.toString() ?? "", volumeKind: request.volumeKind ?? "",
            netWeight: decimalInput(request.netWeight), grossWeight: decimalInput(request.grossWeight), paymentMethod: request.paymentMethod,
            customerOrder: request.customerOrder ?? "", additionalInfo: request.additionalInfo ?? "",
          });
          setInstallments(request.installments.map((x) => ({ key: newKey(), dueDate: x.dueDate.slice(0, 10), value: decimalInput(x.value) }))
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate)));
          if (request.fiscoText != null) { setFiscoText(request.fiscoText); setFiscoEdited(true); }
          setRows(src
            ? rowsFromSource(src, own).map((r) => ({ ...r, remaining: (r.remaining ?? 0) + (own[r.sourceItemNumber!]?.q ?? 0) }))
            : request.items.map((x) => ({ key: newKey(), ...x, quantity: decimalInput(x.quantity), unitPrice: decimalInput(x.unitPrice) })));
          const [branches, chosen] = await Promise.all([
            request.companyId ? getClientBranches(request.companyId).catch(() => []) : Promise.resolve([]),
            getBillingSetting(request.billingSettingId).catch((e: any) => {
              toast(e?.response?.status === 404
                ? { status: "warning", title: "Parametrização salva não encontrada", description: "Escolha a parametrização fiscal de novo." }
                : { status: "error", title: "Não foi possível carregar a parametrização salva", description: "Feche e abra a venda de novo." });
              return null;
            }),
          ]);
          if (!active) return;
          const branch = branches.find((b) => b.id === request.branchId) ?? null;
          lastBranch.current = branch?.id ?? null;
          setSelection({
            companyId: request.companyId ?? null, companyName: "", branch,
            settings: { [request.sourceInvoiceId ? "encomenda" : "venda"]: chosen },
          });
        } else if (src) {
          setIssuerId(src.issuerId);
          if (src.recipient) setRecipient(src.recipient);
          setRows(rowsFromSource(src).filter((r) => (r.remaining ?? 0) > 0));
          setSelection({ ...emptySelection, companyId: src.clientCompanyId ?? null, companyName: src.clientName ?? "" });
        } else {
          setIssuerId((list.find((i) => i.unitType === 2) ?? list[0])?.id ?? "");
        }
        // Venda nova: frete/pagamento sugeridos pelo emitente escolhido
        if (!request) applyDefaults(list.find((i) => i.id === (src?.issuerId ?? (list.find((x) => x.unitType === 2) ?? list[0])?.id)));
      } catch (e: any) {
        if (!active) return;
        setFailed(true);
        toast({ status: "error", title: e?.response?.status === 403 ? "Sem permissão para emitir notas fiscais" : "Não foi possível abrir a venda" });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [invoiceId, entryId]);

  function applyDefaults(issuer?: FiscalIssuer) {
    const d = issuer?.saleDefaults;
    if (d) setForm((f) => ({ ...f, freightMode: d.freightMode, paymentMethod: d.paymentMethod }));
  }

  // Filial escolhida = destinatário (cadastro fiscal salvo, senão Receita)
  useEffect(() => {
    const branch = selection.branch;
    if (!branch?.id || branch.id === lastBranch.current) return;
    lastBranch.current = branch.id;
    if (invoiceId && !userPickedBranch.current) return;
    const before = recipient.document.replace(/\D/g, "");
    if (before === branch.cnpj) return;
    let active = true;
    lookupParty(branch.cnpj).then(({ party, message }) => {
      if (!active) return;
      // Só troca se ninguém mexeu no documento enquanto buscava
      if (party) setRecipient((current) => (current.document.replace(/\D/g, "") === before ? party : current));
      else if (message) toast({ status: message.status, title: message.title, description: message.description });
    });
    return () => { active = false; };
  }, [selection.branch?.id]);

  // Busca de produtos já importados (venda própria)
  useEffect(() => {
    if (onOrder || !issuerId || search.trim().length < 2) { setProducts([]); return; }
    let active = true;
    const t = setTimeout(() => {
      getSaleProducts(issuerId, search.trim()).then((list) => active && setProducts(list)).catch(() => active && setProducts([]));
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [search, issuerId, onOrder]);

  const addProduct = (p: SaleProduct) => {
    touch();
    setRows((list) => [...list, {
      key: newKey(), productCode: p.productCode, description: p.description, ncm: p.ncm, cest: p.cest, unit: p.unit, origin: p.origin,
      additionalInfo: p.additionalInfo, unitCost: p.unitCost, quantity: "", unitPrice: "",
    }]);
    setSearch("");
    setProducts([]);
  };
  const patchRow = (key: string, field: "quantity" | "unitPrice", value: string) => {
    touch();
    setRows((list) => list.map((r) => (r.key === key ? { ...r, [field]: value.replace(/[^\d.,]/g, "") } : r)));
  };

  // O servidor ordena as parcelas por vencimento (e numera nessa ordem): a tela mostra igual
  const sortedInstallments = useMemo(() => [...installments].sort((a, b) => a.dueDate.localeCompare(b.dueDate)), [installments]);

  // Pedido para o servidor (null = ainda inválido na tela)
  const built = useMemo((): { request: SaleRequest | null; problem: string | null } => {
    const money = [form.freight, form.insurance, form.discount, form.otherExpenses].map(parseMoney);
    const weights = [form.netWeight, form.grossWeight].map(parseWeight);
    const lines = rows.filter((r) => r.quantity.trim() !== "" || !onOrder);
    const parsed = lines.map((r) => ({ r, q: parseDecimal(r.quantity), p: parseMoney(r.unitPrice) }));
    const values = sortedInstallments.map((x) => parseMoney(x.value));
    if (money.some(bad) || weights.some(bad) || parsed.some((x) => bad(x.q) || bad(x.p)) || values.some(bad))
      return { request: null, problem: "Há número inválido: use vírgula para decimais (ex.: 1.250,00 ou 1,25); valor como \"1.250\" é ambíguo, escreva 1250 ou 1.250,00." };
    if (!issuerId) return { request: null, problem: "Escolha o emitente." };
    if (recipient.document.replace(/\D/g, "").length < 11) return { request: null, problem: "Informe o CPF/CNPJ do destinatário." };
    if (!setting?.id) return { request: null, problem: "Escolha a parametrização fiscal." };
    if (parsed.length === 0) return { request: null, problem: onOrder ? "Informe a quantidade de pelo menos um item." : "Adicione produtos." };
    if (parsed.some((x) => !x.q || !x.p)) return { request: null, problem: "Informe quantidade e valor unitário de cada item." };
    if (sortedInstallments.some((x) => !x.dueDate)) return { request: null, problem: "Parcela sem vencimento." };
    return {
      problem: null,
      request: {
        issuerId, billingSettingId: setting.id, sourceInvoiceId: sourceId, companyId: selection.companyId, branchId: selection.branch?.id ?? null,
        recipient, carrier: withCarrier && carrier.name.trim() ? carrier : null, freightMode: form.freightMode,
        freight: money[0] ?? 0, insurance: money[1] ?? 0, discount: money[2] ?? 0, otherExpenses: money[3] ?? 0,
        volumeQuantity: form.volumeQuantity ? Number(form.volumeQuantity) : null, volumeKind: form.volumeKind || null,
        netWeight: weights[0], grossWeight: weights[1], paymentMethod: form.paymentMethod,
        installments: sortedInstallments.map((x, i) => ({ dueDate: isoDate(x.dueDate), value: values[i] })),
        customerOrder: form.customerOrder.trim() || null, fiscoText: fiscoEdited ? fiscoText.trim() || null : null,
        additionalInfo: form.additionalInfo.trim() || null,
        items: parsed.map(({ r, q, p }) => ({
          sourceItemNumber: r.sourceItemNumber ?? null, productCode: r.productCode, description: r.description, ncm: r.ncm, cest: r.cest,
          unit: r.unit, quantity: q!, unitPrice: p!, origin: r.origin ?? null, additionalInfo: r.additionalInfo ?? null,
        })),
      },
    };
  }, [rows, form, sortedInstallments, issuerId, setting?.id, sourceId, selection.companyId, selection.branch?.id, recipient, withCarrier, carrier, fiscoEdited, fiscoText, onOrder]);

  // Prévia ao vivo (o cálculo que vale é o do servidor)
  const requestKey = built.request ? JSON.stringify(built.request) : "";
  useEffect(() => {
    // Invalida a resposta que ainda estiver a caminho
    const n = ++previewSeq.current;
    setPreviewError(null);
    if (!built.request) { setPreview(null); setCalculating(false); return; }
    setCalculating(true);
    const key = requestKey;
    const t = setTimeout(() => {
      previewSale(built.request!, invoiceId)
        .then((p) => { if (n === previewSeq.current) { setPreview(p); setPreviewKey(key); } })
        .catch((e: any) => {
          if (n !== previewSeq.current) return;
          setPreview(null);
          const data = e?.response?.data;
          const list: string[] = Array.isArray(data?.errors) ? data.errors : data?.errors ? (Object.values(data.errors).flat() as string[]) : [];
          setPreviewError(list.length ? list.join(" ") : data?.title ?? "Não foi possível calcular a venda.");
        })
        .finally(() => { if (n === previewSeq.current) setCalculating(false); });
    }, 600);
    return () => clearTimeout(t);
  }, [requestKey, retry]);

  useEffect(() => {
    if (!fiscoEdited && preview?.fiscoTemplateText != null) setFiscoText(preview.fiscoTemplateText);
  }, [preview?.fiscoTemplateText, fiscoEdited]);

  const generateInstallments = () => {
    const count = Math.min(Math.max(Number(split.count) || 0, 1), 120);
    const every = Math.max(Number(split.every) || 0, 0);
    touch();
    setInstallments(Array.from({ length: count }, (_, i) => ({ key: newKey(), dueDate: addDays(every * (i + 1)), value: "" })));
  };

  const save = async () => {
    if (!built.request) return;
    setSaving(true);
    const result = invoiceId ? await updateSale(invoiceId, built.request) : await createSale(built.request);
    setSaving(false);
    if (!result.success || !result.invoice) {
      toast({ status: "error", title: "Não foi possível salvar a venda", description: result.errors.join("\n"), duration: 10000, isClosable: true });
      return;
    }
    if (result.warnings.length)
      toast({ status: "warning", title: "Rascunho salvo com avisos", description: result.warnings.join("\n"), duration: 10000, isClosable: true });
    else toast({ status: "success", title: "Rascunho da venda salvo" });
    onSaved(result.invoice.id);
  };

  const close = () => (dirty ? setConfirmClose(true) : onClose());
  const fresh = !!preview?.success && previewKey === requestKey && !calculating;
  const draftItems = fresh ? preview!.draft?.items ?? [] : [];
  /** Posição do item no pedido (encomenda manda só as linhas com quantidade): casa com o item da prévia. */
  const lineIndex = (key: string) => rows.filter((r) => r.quantity.trim() !== "" || !onOrder).findIndex((r) => r.key === key);
  const issuer = issuers.find((i) => i.id === issuerId);
  const t = fresh ? preview!.totals : null;

  return (
    <Modal isOpen onClose={close} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px" maxW="1280px">
        <ModalHeader fontSize="17px">
          {invoiceId ? "Editar venda" : onOrder ? "Venda encomenda" : "Nova venda"}
          {source?.entryNumber ? ` · entrada nº ${source.entryNumber}` : ""}{source?.processRef ? ` · ${source.processRef}` : ""}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {failed ? (
            <Text fontSize="13px" color={crm.textMuted} py="24px" textAlign="center">Não foi possível abrir a venda. Feche e tente de novo.</Text>
          ) : loading ? (
            <Flex justify="center" py="32px"><Spinner /></Flex>
          ) : (
            <Box>
              {(source?.warnings ?? []).map((w, i) => (
                <Alert key={i} status="warning" borderRadius="10px" mb="8px" fontSize="13px"><AlertIcon />{w}</Alert>
              ))}
              {!!source?.existing.length && !invoiceId && (
                <Alert status="info" borderRadius="10px" mb="8px" fontSize="13px">
                  <AlertIcon />
                  Esta entrada já tem {source.existing.length} venda(s): {source.existing.map((nf) => `${nf.number ? `nº ${nf.number}` : "rascunho"} (${statusInfo[nf.status]?.label ?? nf.status})`).join(", ")}.
                </Alert>
              )}

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px" mb="12px">
                <FormControl>
                  <Label>Emitente</Label>
                  <Select size="sm" value={issuerId} isDisabled={onOrder || !!invoiceId}
                    onChange={(e) => { touch(); setIssuerId(e.target.value); setRows([]); applyDefaults(issuers.find((i) => i.id === e.target.value)); }}>
                    {issuers.map((i) => <option key={i.id!} value={i.id!}>{i.name} · {i.state ?? ""}{i.environment === "homologacao" ? " · homologação" : ""}</option>)}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <ClientBranchPicker kind={kind} emptyLabel="Escolha a parametrização fiscal" value={selection} onChange={setSelection} autoPick={!invoiceId}
                onUserChange={(what) => { touch(); if (what !== "setting") userPickedBranch.current = true; }} />

              <SectionTitle>Destinatário</SectionTitle>
              <FiscalPartyFields value={recipient} onChange={(v) => { touch(); setRecipient(v); }} />

              <SectionTitle>Itens</SectionTitle>
              {!onOrder && (
                <Box position="relative" mb="10px" maxW="560px">
                  <Input size="sm" placeholder="Buscar produto já importado (código, descrição ou NCM)" value={search}
                    isDisabled={!issuerId} onChange={(e) => setSearch(e.target.value)} />
                  {products.length > 0 && (
                    <Box position="absolute" zIndex={10} top="34px" left={0} right={0} bg="white" border={`1px solid ${crm.border}`}
                      borderRadius="10px" boxShadow="md" maxH="280px" overflowY="auto">
                      {products.map((p, i) => (
                        <Box key={`${p.productCode}-${p.ncm}-${p.entryNumber ?? i}`} px="10px" py="7px" cursor="pointer" _hover={{ bg: crm.subtle }} onClick={() => addProduct(p)} fontSize="13px">
                          <Text fontWeight="600" noOfLines={1}>{p.productCode} · {p.description}</Text>
                          <Text color={crm.textMuted} fontSize="12px">
                            NCM {formatNcm(p.ncm)} · {p.unit} · custo {brl(p.unitCost)}{p.entryNumber ? ` · entrada nº ${p.entryNumber}` : ""}{p.processRef ? ` · ${p.processRef}` : ""}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              {onOrder && rows.length > 0 && (
                <Flex justify="flex-end" mb="6px">
                  <Button size="xs" variant="outline" onClick={() => { touch(); setRows((l) => l.map((r) => ((r.remaining ?? 0) > 0 ? { ...r, quantity: decimalInput(r.remaining) } : r))); }}>
                    Vender todo o saldo
                  </Button>
                </Flex>
              )}
              <Box overflowX="auto">
                <TableCard>
                  <Thead>
                    <Tr>
                      <Th {...thProps}>Produto</Th>
                      <Th {...thProps}>NCM / CFOP</Th>
                      {onOrder && <Th {...thProps} isNumeric>Saldo</Th>}
                      <Th {...thProps} isNumeric>Qtd.</Th>
                      <Th {...thProps} isNumeric>Valor unit.</Th>
                      <Th {...thProps} isNumeric>Custo ref.</Th>
                      <Th {...thProps} isNumeric>Valor produto</Th>
                      <Th {...thProps} isNumeric>ICMS</Th>
                      <Th {...thProps} isNumeric>ST</Th>
                      <Th {...thProps} isNumeric>IPI</Th>
                      <Th {...thProps} isNumeric>PIS / COFINS</Th>
                      {!onOrder && <Th {...thProps} />}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.length === 0 && (
                      <Tr><Td {...tdProps} colSpan={12} color={crm.textMuted}>{onOrder ? "Sem saldo nesta NF de entrada." : "Busque e adicione os produtos."}</Td></Tr>
                    )}
                    {rows.map((r) => {
                      const k = lineIndex(r.key);
                      const d = k >= 0 ? draftItems[k] : undefined;
                      const q = parseDecimal(r.quantity);
                      const over = onOrder && q != null && r.remaining != null && q > r.remaining + 0.00001;
                      return (
                        <Tr key={r.key}>
                          <Td {...tdProps} maxW="280px">
                            <Text fontWeight="600" noOfLines={1}>{r.productCode}</Text>
                            <Text fontSize="12px" color={crm.textMuted} noOfLines={2}>{r.description}</Text>
                          </Td>
                          <Td {...tdProps} whiteSpace="nowrap">{formatNcm(r.ncm)}{d ? ` · ${d.cfop}` : ""}{d?.taxes.icmsCst ? ` · CST ${d.taxes.icmsCst}` : ""}</Td>
                          {onOrder && <Td {...tdProps} isNumeric whiteSpace="nowrap">{fmtQty(r.remaining ?? 0)} {r.unit}</Td>}
                          <Td {...tdProps} minW="100px">
                            <Input size="xs" textAlign="right" isInvalid={over || bad(q)} value={r.quantity} onChange={(e) => patchRow(r.key, "quantity", e.target.value)} />
                          </Td>
                          <Td {...tdProps} minW="120px">
                            <Input size="xs" textAlign="right" isInvalid={bad(parseMoney(r.unitPrice))} value={r.unitPrice} onChange={(e) => patchRow(r.key, "unitPrice", e.target.value)} />
                          </Td>
                          <Td {...tdProps} isNumeric color={crm.textMuted}>{r.unitCost ? brl(r.unitCost) : "—"}</Td>
                          <Td {...tdProps} isNumeric>{d ? brl(d.grossValue) : "—"}</Td>
                          <Td {...tdProps} isNumeric>{d ? brl(d.taxes.icmsValue ?? 0) : "—"}</Td>
                          <Td {...tdProps} isNumeric>{d ? brl(d.taxes.icmsStValue ?? 0) : "—"}</Td>
                          <Td {...tdProps} isNumeric>{d ? brl(d.taxes.ipiValue ?? 0) : "—"}</Td>
                          <Td {...tdProps} isNumeric whiteSpace="nowrap">{d ? `${brl(d.taxes.pisValue ?? 0)} / ${brl(d.taxes.cofinsValue ?? 0)}` : "—"}</Td>
                          {!onOrder && (
                            <Td {...tdProps}>
                              <IconButton aria-label="Remover item" size="xs" variant="ghost" icon={<FaTrash />}
                                onClick={() => { touch(); setRows((l) => l.filter((x) => x.key !== r.key)); }} />
                            </Td>
                          )}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </TableCard>
              </Box>

              <SimpleGrid columns={{ base: 2, md: 4 }} spacing="10px" mt="12px">
                {([["freight", "Frete (R$)"], ["insurance", "Seguro (R$)"], ["discount", "Desconto (R$)"], ["otherExpenses", "Outras despesas (R$)"]] as const).map(([f, label]) => (
                  <FormControl key={f} isInvalid={bad(parseMoney(form[f]))}>
                    <Label>{label}</Label>
                    <Input size="sm" inputMode="decimal" value={form[f]} onChange={(e) => set(f, e.target.value)} />
                  </FormControl>
                ))}
              </SimpleGrid>
              <Text mt="4px" fontSize="12px" color={crm.textMuted}>Rateados pelos itens pelo valor do produto; entram nas bases de ICMS, ST, IPI e PIS/COFINS.</Text>

              <Flex align="center" justify="space-between" mt="18px" mb="8px">
                <Text fontSize="13px" fontWeight="700">Transporte</Text>
                <Button variant="link" size="sm" onClick={() => { touch(); setWithCarrier((v) => !v); }}>
                  {withCarrier ? "Sem transportadora" : "Informar transportadora"}
                </Button>
              </Flex>
              {withCarrier && <FiscalPartyFields carrier value={carrier} onChange={(v) => { touch(); setCarrier(v); }} />}
              <SimpleGrid columns={{ base: 2, md: 5 }} spacing="10px" mt="10px">
                <FormControl>
                  <Label>Frete</Label>
                  <Select size="sm" value={form.freightMode} onChange={(e) => set("freightMode", Number(e.target.value))}>
                    <option value={0}>0 · remetente</option>
                    <option value={1}>1 · destinatário</option>
                    <option value={2}>2 · terceiros</option>
                    <option value={9}>9 · sem frete</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <Label>Volumes</Label>
                  <Input size="sm" inputMode="numeric" value={form.volumeQuantity} onChange={(e) => set("volumeQuantity", e.target.value.replace(/\D/g, ""))} />
                </FormControl>
                <FormControl>
                  <Label>Espécie</Label>
                  <Input size="sm" maxLength={60} value={form.volumeKind} onChange={(e) => set("volumeKind", e.target.value)} />
                </FormControl>
                <FormControl isInvalid={bad(parseWeight(form.netWeight))}>
                  <Label>Peso líquido (kg)</Label>
                  <Input size="sm" inputMode="decimal" value={form.netWeight} onChange={(e) => set("netWeight", e.target.value)} />
                </FormControl>
                <FormControl isInvalid={bad(parseWeight(form.grossWeight))}>
                  <Label>Peso bruto (kg)</Label>
                  <Input size="sm" inputMode="decimal" value={form.grossWeight} onChange={(e) => set("grossWeight", e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <SectionTitle>Pagamento</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px" alignItems="end">
                <FormControl>
                  <Label>Forma de pagamento</Label>
                  <Select size="sm" value={form.paymentMethod} onChange={(e) => set("paymentMethod", e.target.value)}>
                    {PAYMENT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <Label>Pedido do cliente</Label>
                  <Input size="sm" maxLength={15} value={form.customerOrder} onChange={(e) => set("customerOrder", e.target.value)} />
                </FormControl>
                <Flex gap="6px" align="end" gridColumn={{ md: "span 2" }}>
                  <FormControl w="90px">
                    <Label>Parcelas</Label>
                    <Input size="sm" inputMode="numeric" value={split.count} onChange={(e) => setSplit((s) => ({ ...s, count: e.target.value.replace(/\D/g, "") }))} />
                  </FormControl>
                  <FormControl w="110px">
                    <Label>A cada (dias)</Label>
                    <Input size="sm" inputMode="numeric" value={split.every} onChange={(e) => setSplit((s) => ({ ...s, every: e.target.value.replace(/\D/g, "") }))} />
                  </FormControl>
                  <Button size="sm" variant="outline" onClick={generateInstallments}>Gerar parcelas</Button>
                  {installments.length > 0 && <Button size="sm" variant="ghost" onClick={() => { touch(); setInstallments([]); }}>Sem parcelas</Button>}
                </Flex>
              </SimpleGrid>
              {installments.length > 0 && (
                <Box mt="10px" maxW="560px">
                  <TableCard>
                    <Thead>
                      <Tr>
                        <Th {...thProps}>Nº</Th>
                        <Th {...thProps}>Vencimento</Th>
                        <Th {...thProps} isNumeric>Valor (vazio = divide o resto)</Th>
                        <Th {...thProps} isNumeric>Calculado</Th>
                        <Th {...thProps} />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {installments.map((x) => { const i = sortedInstallments.indexOf(x); return (
                        <Tr key={x.key}>
                          <Td {...tdProps}>{String(i + 1).padStart(3, "0")}</Td>
                          <Td {...tdProps}>
                            <Input size="xs" type="date" value={x.dueDate}
                              onChange={(e) => { touch(); setInstallments((l) => l.map((y) => (y.key === x.key ? { ...y, dueDate: e.target.value } : y))); }} />
                          </Td>
                          <Td {...tdProps}>
                            <Input size="xs" textAlign="right" isInvalid={bad(parseMoney(x.value))} value={x.value}
                              onChange={(e) => { touch(); setInstallments((l) => l.map((y) => (y.key === x.key ? { ...y, value: e.target.value } : y))); }} />
                          </Td>
                          <Td {...tdProps} isNumeric>{fresh ? brl(preview!.draft?.installments?.[i]?.value ?? 0) : "—"}</Td>
                          <Td {...tdProps}>
                            <IconButton aria-label="Remover parcela" size="xs" variant="ghost" icon={<FaTrash />}
                              onClick={() => { touch(); setInstallments((l) => l.filter((y) => y.key !== x.key)); }} />
                          </Td>
                        </Tr>
                      ); })}
                    </Tbody>
                  </TableCard>
                  <Button mt="6px" size="xs" variant="link" onClick={() => { touch(); setInstallments((l) => [...l, { key: newKey(), dueDate: addDays(30 * (l.length + 1)), value: "" }]); }}>
                    Adicionar parcela
                  </Button>
                </Box>
              )}

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px" mt="14px">
                <FormControl>
                  <Label>Informações ao fisco {!fiscoEdited && !fresh && <Text as="span" fontWeight="400">(atualizando)</Text>} {fiscoEdited && <Button variant="link" size="xs" ml="6px" onClick={() => { touch(); setFiscoEdited(false); }}>usar o da parametrização</Button>}</Label>
                  <Textarea size="sm" rows={3} maxLength={2000} value={fiscoText} onChange={(e) => { touch(); setFiscoText(e.target.value); setFiscoEdited(true); }} />
                </FormControl>
                <FormControl>
                  <Label>Informações complementares (vazio = "PEDIDO" + nº do pedido)</Label>
                  <Textarea size="sm" rows={3} maxLength={5000} value={form.additionalInfo} onChange={(e) => set("additionalInfo", e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <Box mt="14px">
                {built.problem && <Text fontSize="13px" color={crm.textMuted}>{built.problem}</Text>}
                {previewError && (
                  <Alert status="error" borderRadius="10px" mb="6px" fontSize="13px">
                    <AlertIcon />
                    <Text flex="1">{previewError}</Text>
                    <Button size="xs" variant="outline" ml="8px" onClick={() => setRetry((n) => n + 1)}>Recalcular</Button>
                  </Alert>
                )}
                {preview && !preview.success && preview.errors.map((e, i) => (
                  <Alert key={i} status="error" borderRadius="10px" mb="6px" fontSize="13px"><AlertIcon />{e}</Alert>
                ))}
                {preview?.success && preview.warnings.map((w, i) => (
                  <Alert key={i} status="warning" borderRadius="10px" mb="6px" fontSize="13px"><AlertIcon />{w}</Alert>
                ))}
              </Box>
              <Flex mt="8px" gap="16px" wrap="wrap" justify="flex-end" align="center" fontSize="13px" color={crm.textMuted}>
                {calculating && <Spinner size="xs" />}
                {t && (
                  <>
                    <Text>Produtos: <b>{brl(t.products)}</b></Text>
                    {t.freight + t.insurance + t.otherExpenses > 0 && <Text>Frete/seguro/outras: <b>{brl(t.freight + t.insurance + t.otherExpenses)}</b></Text>}
                    {t.discount > 0 && <Text>Desconto: <b>{brl(t.discount)}</b></Text>}
                    <Text>ICMS: <b>{brl(t.icms ?? 0)}</b></Text>
                    <Text>ST: <b>{brl(t.icmsSt + (t.fcpSt ?? 0))}</b></Text>
                    <Text>IPI: <b>{brl(t.ipi)}</b></Text>
                    <Text>PIS/COFINS: <b>{brl(t.pis)} / {brl(t.cofins)}</b></Text>
                    <Text color={crm.text} fontSize="15px">Total da nota: <b>{brl(t.total)}</b></Text>
                  </>
                )}
              </Flex>
              {issuer?.environment === "homologacao" && (
                <Text mt="6px" fontSize="12px" color="purple.500" textAlign="right">Emitente em homologação: a nota sai sem valor fiscal.</Text>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={close}>Cancelar</Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving}
            isDisabled={!built.request || !fresh} onClick={save}>
            {invoiceId ? "Salvar rascunho" : "Gerar rascunho"}
          </Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog isOpen={confirmClose} leastDestructiveRef={cancelRef} onClose={() => setConfirmClose(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="16px">Descartar alterações?</AlertDialogHeader>
            <AlertDialogBody fontSize="14px">A venda ainda não foi salva.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmClose(false)}>Voltar</Button>
              <Button colorScheme="red" ml={3} onClick={() => { setConfirmClose(false); onClose(); }}>Descartar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default SaleModal;
